/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { ProfileModel } from '@spm/universal-access';
import { withRouter, Link as RouterLink } from 'react-router-dom';
import {
  Header,
  PrimaryNavigation,
  SecondaryNavigation,
  Tabs,
  Tab,
} from '@govhhs/govhhs-design-system-react';
import PATHS from '@spm/universal-access-ui/src/router/Paths';
import logo from '../../../img/dsslogo.png';
import translations from './ApplicationHeaderMessages';
import * as ApplicationHeaderLogic from './ApplicationHeaderLogic';
import { ID, NAVIGATION_HEADER_TABS } from '../constants';
import SDPATHS from '../../../paths';
import SDMFALogoutButton from '../../SDMFA/SDMFALogoutButton';
import loginButton from '../../../img/loginbutton.png';

/**
 * Returns true if the Your Account tab is active for the current url pathname. If
 * true the primary navigation contains specific navigation tabs.
 *
 * @param {*} urlPathname current url pathname.
 * @returns true if tab should be active.
 */
const isYourAccountTabActive = urlPathname =>
  ApplicationHeaderLogic.isTabActiveForUrlPathname(
    urlPathname,
    NAVIGATION_HEADER_TABS.YOUR_ACCOUNT.NAME
  );

/**
 * Returns the ApplicationHeaderComponent.
 *
 * @param {*} props props passed to this component.
 * @returns {Component} ApplicationHeaderComponent.
 */
const ApplicationHeaderComponent = props => {
  const {
    intl,
    location,
    profile: userProfile,
    hasErrorBeenCaught,
    isUserCanAppeal,
    isALinkedUser,
    isEmpty,
    mainContentId,
  } = props;

  const { formatMessage } = intl;
  const { pathname: currentUrlPathname } = location;
  const { getActivePrimaryNavigationTabId, getActiveSecondaryNavigationTabId } =
    ApplicationHeaderLogic;

  const marginDirection = document.dir === 'rtl' ? { marginRight: 'auto' } : { marginLeft: 'auto' };

  const appealsTab = (
    <Tab
      data-testid={NAVIGATION_HEADER_TABS.APPEALS.ID}
      id={NAVIGATION_HEADER_TABS.APPEALS.ID}
      label={formatMessage(translations.headerAppealsLabel)}
      link={<RouterLink to={PATHS.APPEALS.ROOT} />}
    />
  );

  const skipProps = {};
  if (mainContentId) {
    skipProps.mainContentId = mainContentId;
    skipProps.skipNavigationLinkText = intl.formatMessage(translations.skipNav);
  }

  if (isEmpty) {
    return (
      <Header
        key="application-header"
        data-testid={ID.APPLICATION_HEADER_COMPONENT_ID}
        // BEGIN SD:1039 Yp: updated height, width and resizemode within logo attribute
        logo={
          <img
            alt="agency logo"
            aria-hidden
            height="100%"
            id="sso-verifier-header"
            resizemode="contain"
            src={logo}
            width="100%"
          />
        }
        // BEGIN SD:1039 Yp: updated height, width and resizemode within logo attribute
        menuHidden
        // commented this out since we added "cabinet secretary" logo,so dont need this title.
        // title={formatMessage(translations.headerLandingPageTitle)}
        titleHoverText={formatMessage(translations.headerHomeLabel)}
        type="scrollable"
        {...skipProps}
      >
        <span />
      </Header>
    );
  }
  return (
    <Header
      key="application-header"
      data-testid={ID.APPLICATION_HEADER_COMPONENT_ID}
      // START SD-6466 - AK - fix link to rurl
      // link={<RouterLink to={PATHS.HOME} />}
      link={<a href="https://dss.sd.gov/" />}
      logo={
        <img
          alt="agency logo"
          aria-hidden
          className="logo-image"
          data-testid={ID.APPLICATION_HEADER_HOME_LINK_ID}
          height="100%"
          id={ID.APPLICATION_HEADER_HOME_LINK_ID}
          resizemode="contain"
          src={logo}
          width="100%"
        />
      }
      // END SD-6466 - AK - fix link to rurl
      // BEGIN SD:1039 Yp: added div, updated height, width and resizemode within logo attribute
      menuButtonTitle={formatMessage(translations.headerMenuLabel)}
      // END SD:1039 Yp: added div, updated height, width and resizemode within logo attribute
      target="_blank"
      // commented this out since we added "cabinet secretary" logo,so dont need this title.
      // title={formatMessage(translations.headerLandingPageTitle)}
      titleHoverText={formatMessage(translations.headerHomeLabel)}
      type="scrollable"
      {...skipProps}
    >
      {window.screen.width > 425 && (
        <div className="cabinet-secretary">
          {/** Begin - SD-6478 - Moving hard-coded values to message file */}
          <h6 className="cabinet-secretary-text">
            {formatMessage(translations.cabinetSecretaryLabel)}{' '}
          </h6>
          <h6 className="cabinet-secretary-text">
            {formatMessage(translations.cabinetSecretaryName)}
          </h6>
          {/** End - SD-6478 - Moving hard-coded values to message file */}
        </div>
      )}
      <PrimaryNavigation type="scrollable">
        {isYourAccountTabActive(currentUrlPathname) && (
          <Tabs
            key="primary-tabs"
            ariaLabel="primary-tabs"
            current={getActivePrimaryNavigationTabId(currentUrlPathname)}
            scrollable
          >
            <Tab
              data-testid={NAVIGATION_HEADER_TABS.DASHBOARD.ID}
              id={NAVIGATION_HEADER_TABS.DASHBOARD.ID}
              label={formatMessage(translations.headerDashboardLabel)}
              link={<RouterLink to={PATHS.ACCOUNT} />}
            />
            <Tab
              data-testid={NAVIGATION_HEADER_TABS.YOUR_BENEFITS.ID}
              id={NAVIGATION_HEADER_TABS.YOUR_BENEFITS.ID}
              label={formatMessage(translations.headerYourBenefitsLabel)}
              link={<RouterLink to={PATHS.BENEFITS} />}
            />
            {/** BEGIN SD-6517 - MS - Enabling documents tab for all users */}
            {
              <Tab
                data-testid={NAVIGATION_HEADER_TABS.VERIFICATIONS.ID}
                id={NAVIGATION_HEADER_TABS.VERIFICATIONS.ID}
                label={formatMessage(translations.headerYourVerificationsText)}
                link={<RouterLink to={PATHS.VERIFICATIONS.ROOT} />}
              />
            }
            {/** END SD-6517 - MS - Enabling documents tab for all users */}
            {/** BEGIN SD-6517 - MS - Setting Report Changes to only display for linked users */}
            {isALinkedUser() && (
              <Tab
                data-testid={NAVIGATION_HEADER_TABS.PROFILE.ID}
                id={NAVIGATION_HEADER_TABS.PROFILE.ID}
                label={formatMessage(translations.headerProfileText)}
                link={<RouterLink to={PATHS.PROFILE} />}
              />
            )}
            {/** BEGIN SD-6517 - MS - Setting Report Changes to only display for linked users */}
            {isALinkedUser() && (
              <Tab
                data-testid={NAVIGATION_HEADER_TABS.NOTICES.ID}
                id={NAVIGATION_HEADER_TABS.NOTICES.ID}
                label={formatMessage(translations.headerNoticesLabel)}
                link={<RouterLink to={PATHS.NOTICES.ROOT} />}
              />
            )}
            {isUserCanAppeal && appealsTab}
            {/** BEGIN SD-6517 - MS - Setting local office to only display for linked users */}
            {isALinkedUser() && (
              <Tab
                data-testid={NAVIGATION_HEADER_TABS.LOCAL_OFFICE.ID}
                id={NAVIGATION_HEADER_TABS.LOCAL_OFFICE.ID}
                label={formatMessage(translations.headerLocalOfficeLabel)}
                link={<RouterLink to={SDPATHS.LOCAL_OFFICE} />}
              />
            )}
            {
              <Tab
                data-testid={NAVIGATION_HEADER_TABS.SETTINGS.ID}
                id={NAVIGATION_HEADER_TABS.SETTINGS.ID}
                label={formatMessage(translations.headerSettingsLabel)}
                link={<RouterLink to={SDPATHS.SETTINGS} />}
              />
            }
            {/** END SD-6517 - MS - Setting local office to only display for linked users */}
          </Tabs>
        )}
      </PrimaryNavigation>
      <SecondaryNavigation type="scrollable">
        <Tabs
          key="secondary-tabs"
          ariaLabel="secondary-tabs"
          className="wds-u-mr--small"
          current={getActiveSecondaryNavigationTabId(currentUrlPathname)}
        >
          <Tab
            data-testid={NAVIGATION_HEADER_TABS.HOME.ID}
            id={NAVIGATION_HEADER_TABS.HOME.ID}
            label={formatMessage(translations.headerHomeLabel)}
            link={<RouterLink to={PATHS.HOME} />}
          />
          <Tab
            data-testid={NAVIGATION_HEADER_TABS.BENEFITS.ID}
            id={NAVIGATION_HEADER_TABS.BENEFITS.ID}
            label={formatMessage(translations.headerBenefitsLabel)}
            link={<RouterLink to={PATHS.APPLY} />}
          />
          {userProfile && (
            <Tab
              data-testid={NAVIGATION_HEADER_TABS.YOUR_ACCOUNT.ID}
              id={NAVIGATION_HEADER_TABS.YOUR_ACCOUNT.ID}
              label={formatMessage(translations.headerYourAccountLabel)}
              link={<RouterLink to={PATHS.ACCOUNT} />}
            />
          )}
        </Tabs>
        {/* Show profile menu or signed out menu */}
        {userProfile ? (
          <>
            {/** BEGIN - SD-7277 - MS - Adding logout flow for SSO */}
            <SDMFALogoutButton data={props} />
            {/** END - SD-7277 - MS - Adding logout flow for SSO */}
          </>
        ) : (
          !hasErrorBeenCaught && (
            <>
              {/** BEGIN - SD-1041 - Adding button to log in during MFA */}
              <input
                key="login"
                alt="Log In"
                category="link"
                className="loginButton"
                onClick={e => {
                  // This is the mobile screen login button
                  e.preventDefault();
                  props.history.push({
                    pathname: PATHS.LOGIN,
                    params: { previousPathname: location.pathname },
                  });
                }}
                src={loginButton}
                style={marginDirection}
                type="image"
              />

              {/** END - SD-1041 - Adding button to log in during MFA */}
            </>
          )
        )}
      </SecondaryNavigation>
      {!userProfile && !hasErrorBeenCaught && <></>}
    </Header>
  );
};

/**
 * The ApplicationHeaderComponent properties.
 *
 * See https://reactjs.org/docs/react-component.html#props
 *
 * @memberof ApplicationHeaderComponent
 */
ApplicationHeaderComponent.propTypes = {
  hasErrorBeenCaught: PropTypes.bool,
  history: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  isALinkedUser: PropTypes.func.isRequired,
  isAppealsEnabled: PropTypes.bool.isRequired,
  isEmpty: PropTypes.bool,
  isUserCanAppeal: PropTypes.bool,
  location: PropTypes.object.isRequired,
  mainContentId: PropTypes.string,
  profile: PropTypes.instanceOf(ProfileModel),
};

/**
 * The ApplicationHeaderComponent properties.
 *
 * See https://reactjs.org/docs/react-component.html#defaultprops
 *
 * @memberof ApplicationHeaderComponent
 */
ApplicationHeaderComponent.defaultProps = {
  hasErrorBeenCaught: false,
  isEmpty: false,
  isUserCanAppeal: false,
  mainContentId: null,
  profile: null,
};

export default withRouter(injectIntl(ApplicationHeaderComponent));
